// @ts-check
// Formulários das ações no CRM e da integração (contrato de bots, seções 4.14 a 4.18): criar o card de captação com as
// respostas (aluno, curso, período, unidade, origem) e o rodízio, mudar a etapa (nunca Matriculado), alterar campo,
// etiquetas, responsável fixo ou em rodízio, nota, agendar visita (a página de agendamento, o convite e a espera) e as
// ações herdadas das automações (tarefa, aviso à equipe, webhook e requisição HTTP, com a resposta numa variável).
import { html } from 'htm/preact';
import { useMemo } from 'preact/hooks';
import { workspaceMembersState } from '../../../data/workspace-members.js';
import { BOT_RECORD_TARGETS } from '../../../../../shared/bots/bot-definition.js';
import { FlowChecklist } from '../../flows/node-forms/lead-forms.js';
import { AutomationActionFields } from '../../automations/automation-actions-editor.js';
import { automationActionAsConfig, nodeAsAutomationAction } from '../../flows/node-forms/automation-action-form.js';
import { cardField, cardObject, stageOptions } from '../../flows/flow-lookups.js';
import { FlowField, FlowNotice } from '../../flows/flow-ui.js';
import { BotMembersField, BotNumberField, BotSaveToField, BotSelectField, BotTextField } from '../bot-form-fields.js';
import { memberChoices, targetObject } from '../bot-lookups.js';
import { botFieldProblemText, botProblemText } from '../bot-ui.js';
import { BotToggleRow } from './identity-forms.js';
import { translateMetadataLabel, t } from '../../../i18n/index.js';

/** @typedef {import('../bot-node-panel.js').BotNodeFormProps} BotNodeFormProps */

const BLOCKED_STAGES = new Set(['ENROLLED']);
const BLOCKED_UPDATE_FIELDS = new Set(['stage', 'tags', 'effectiveStage', 'stageOverride']);

/** Opções de um campo de seleção do card. @param {string} fieldName */
const fieldOptions = (fieldName) => (cardField(fieldName)?.options ?? []).map((/** @type {any} */ option) => ({ value: option.value, label: translateMetadataLabel(option.label) }));

/** @param {string[]} variables @param {string | null} [current] */
const variableOptions = (variables, current = null) => [...new Set([...variables, ...(current ? [current] : [])])].map((name) => ({ value: name, label: `bot.${name}` }));

/** @param {BotNodeFormProps} props */
export function CreateCaptureCardForm({ node, variables, onConfig }) {
  const config = node.config ?? {};
  /** @param {string} key @param {string} labelKey */
  const variableField = (key, labelKey) => html`<${BotSelectField} key=${key} label=${t(labelKey)} value=${config[key]} placeholder=${t('bots.forms.captureCard.notAsked')}
    options=${variableOptions(variables, config[key])} onChange=${(value) => onConfig({ [key]: value })} />`;
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-capture-card-form">
    <${FlowNotice}>${t('bots.forms.captureCard.notice')}<//>
    ${variableField('studentNameVariable', 'bots.forms.captureCard.studentName')}
    ${variableField('courseVariable', 'bots.forms.captureCard.course')}
    ${variableField('academicPeriodVariable', 'bots.forms.captureCard.academicPeriod')}
    ${variableField('campusVariable', 'bots.forms.captureCard.campus')}
    ${variableField('leadSourceVariable', 'bots.forms.captureCard.leadSource')}
    <${BotToggleRow} value=${config.assign === 'ROUND_ROBIN'} label=${t('bots.forms.captureCard.roundRobin')}
      onChange=${(isRoundRobin) => onConfig({ assign: isRoundRobin ? 'ROUND_ROBIN' : 'NONE' })} />
    ${config.assign === 'ROUND_ROBIN'
      ? html`<${BotMembersField} label=${t('bots.forms.captureCard.members')} members=${memberChoices()} selected=${config.workspaceMemberIds ?? []}
          onChange=${(workspaceMemberIds) => onConfig({ workspaceMemberIds })} />
        <p class="flow-field-hint">${t('bots.forms.captureCard.membersHint')}</p>`
      : null}
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function UpdateStageForm({ node, problems, onConfig }) {
  const config = node.config ?? {};
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-update-stage-form">
    <${BotSelectField} label=${t('bots.forms.stage.stage')} value=${config.stage} placeholder=${t('bots.forms.choose')}
      options=${stageOptions().filter((option) => !BLOCKED_STAGES.has(option.value))} onChange=${(stage) => onConfig({ stage, lostReason: null, notLeadReason: null })}
      error=${botFieldProblemText(problems, 'stage')} hint=${t('bots.forms.stage.hint')} />
    ${config.stage === 'LOST'
      ? html`<${BotSelectField} label=${t('bots.forms.stage.lostReason')} value=${config.lostReason} options=${fieldOptions('lostReason')}
          onChange=${(lostReason) => onConfig({ lostReason })} error=${botFieldProblemText(problems, 'lostReason')} />`
      : null}
    ${config.stage === 'NOT_A_LEAD'
      ? html`<${BotSelectField} label=${t('bots.forms.stage.notLeadReason')} value=${config.notLeadReason} options=${fieldOptions('notLeadReason')}
          onChange=${(notLeadReason) => onConfig({ notLeadReason })} error=${botFieldProblemText(problems, 'notLeadReason')} />`
      : null}
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function UpdateFieldForm({ node, problems, variables, onConfig }) {
  const config = node.config ?? {};
  const target = config.target ?? 'CARD';
  const fields = (targetObject(target)?.fields ?? []).filter((/** @type {any} */ field) => field.isActive !== false && !field.isSystem && !BLOCKED_UPDATE_FIELDS.has(field.name)
    && field.type !== 'RELATION');
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-update-field-form">
    <${BotSelectField} label=${t('bots.forms.saveTo.target')} value=${target} placeholder=${null}
      options=${BOT_RECORD_TARGETS.map((value) => ({ value, label: t(`bots.recordTargets.${value}`) }))} onChange=${(value) => onConfig({ target: value, fieldName: null })} />
    <${BotSelectField} label=${t('bots.forms.saveTo.field')} value=${config.fieldName} options=${fields.map((/** @type {any} */ field) => ({ value: field.name, label: translateMetadataLabel(field.label) }))}
      onChange=${(fieldName) => onConfig({ fieldName })} error=${botFieldProblemText(problems, 'fieldName')} />
    <${BotTextField} label=${t('bots.forms.updateField.value')} value=${config.value} variables=${variables} onChange=${(value) => onConfig({ value })}
      hint=${t('bots.forms.updateField.hint')} />
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function TagsForm({ node, problems, onConfig }) {
  const options = fieldOptions('tags');
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-tags-form">
    <${FlowField} label=${t('bots.forms.tags.tags')} error=${botFieldProblemText(problems, 'tags')}>
      <${FlowChecklist} items=${options.map((option) => ({ id: option.value, label: option.label }))} selected=${node.config?.tags ?? []} label=${t('bots.forms.tags.tags')}
        emptyText=${t('bots.forms.tags.empty')} onChange=${(/** @type {string[]} */ tags) => onConfig({ tags })} />
    <//>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function AssignOwnerForm({ node, problems, onConfig }) {
  const config = node.config ?? {};
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-assign-owner-form">
    <${BotSelectField} label=${t('bots.forms.assignOwner.strategy')} value=${config.strategy ?? 'SPECIFIC'} placeholder=${null}
      options=${['SPECIFIC', 'ROUND_ROBIN'].map((value) => ({ value, label: t(`bots.forms.assignOwner.strategies.${value}`) }))} onChange=${(strategy) => onConfig({ strategy })} />
    <${BotMembersField} label=${t('bots.forms.members')} members=${memberChoices()} selected=${config.workspaceMemberIds ?? []} maximum=${config.strategy === 'ROUND_ROBIN' ? undefined : 1}
      onChange=${(workspaceMemberIds) => onConfig({ workspaceMemberIds })} error=${botFieldProblemText(problems, 'workspaceMemberIds')} />
    <${BotToggleRow} value=${config.onlyIfEmpty === true} label=${t('bots.forms.assignOwner.onlyIfEmpty')} onChange=${(onlyIfEmpty) => onConfig({ onlyIfEmpty })} />
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function AddNoteForm({ node, problems, variables, onConfig }) {
  const config = node.config ?? {};
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-add-note-form">
    <${BotSelectField} label=${t('bots.forms.note.target')} value=${config.target ?? 'CARD'} placeholder=${null}
      options=${['CARD', 'HOUSEHOLD', 'CONTACT'].map((value) => ({ value, label: t(`bots.recordTargets.${value}`) }))} onChange=${(target) => onConfig({ target })} />
    <${BotTextField} label=${t('bots.forms.note.body')} value=${config.body} variables=${variables} multiline rows=${4} maximum=${2000}
      onChange=${(body) => onConfig({ body })} error=${botFieldProblemText(problems, 'body')} />
  </div>`;
}

/**
 * Agendar visita: a página de agendamento (Agendamento integrado) dá os horários livres, da agenda do CRM e do Google
 * Agenda de quem atende; o bloco escolhe a página, a mensagem de convite, a de confirmação e a espera
 * (`{ bookingPageId, text, confirmText, timeoutMinutes }`, a mesma configuração do motor).
 * @param {BotNodeFormProps} props
 */
export function ScheduleVisitForm({ node, definition, problems, variables, lookupData, onConfig }) {
  const config = node.config ?? {};
  const pages = lookupData.bookingPages;
  const options = (pages ?? [])
    .filter((page) => page.isActive || page.id === config.bookingPageId)
    .map((page) => ({
      value: page.id,
      label: page.isActive
        ? t('bots.forms.visit.bookingPageOption', { name: page.name, minutes: page.durationMinutes })
        : t('bots.forms.visit.inactiveOption', { name: page.name }),
    }));
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-schedule-visit-form">
    ${pages !== null && pages.length === 0
      ? html`<${FlowNotice} tone="warning" icon="IconAlertTriangle">${t('bots.forms.visit.noBookingPages')}<//>`
      : null}
    <${BotSelectField} label=${t('bots.forms.visit.bookingPage')} value=${config.bookingPageId} options=${options} placeholder=${t('bots.forms.visit.bookingPagePlaceholder')}
      hint=${t('bots.forms.visit.bookingPageHint')} error=${botFieldProblemText(problems, 'bookingPageId')} testId="bot-visit-booking-page"
      onChange=${(bookingPageId) => onConfig({ bookingPageId })} />
    <${BotTextField} label=${t('bots.forms.visit.text')} value=${config.text} variables=${variables} multiline hint=${t('bots.forms.visit.textHint')}
      onChange=${(text) => onConfig({ text })} error=${botFieldProblemText(problems, 'text')} />
    <${BotTextField} label=${t('bots.forms.visit.confirmText')} value=${config.confirmText} variables=${variables} multiline hint=${t('bots.forms.visit.confirmTextHint')}
      onChange=${(confirmText) => onConfig({ confirmText })} error=${botFieldProblemText(problems, 'confirmText')} />
    <${BotNumberField} label=${t('bots.forms.timeoutMinutes')} value=${config.timeoutMinutes} minimum=${5} maximum=${1380}
      placeholder=${String(definition.settings.questionTimeoutMinutes)} onChange=${(timeoutMinutes) => onConfig({ timeoutMinutes })}
      error=${botFieldProblemText(problems, 'timeoutMinutes')} />
    <${FlowNotice}>${t('bots.forms.visit.notice')}<//>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function AutomationBotForm({ node, problems, onReplaceConfig }) {
  const object = cardObject();
  const action = nodeAsAutomationAction(/** @type {any} */ (node));
  const context = /** @type {any} */ ({
    scope: { objectMetadata: object, triggerType: 'RECORD_UPDATED', triggerSettings: {}, actions: [action] },
    members: workspaceMembersState.value ?? [], templates: null, disabled: false, triggerType: 'RECORD_UPDATED', triggerSettings: {}, whatsappChannels: [],
  });
  const isIntegration = node.type === 'HTTP_REQUEST' || node.type === 'CALL_WEBHOOK';
  const saveResponseTo = node.config?.saveResponseTo ?? null;
  const otherProblems = useMemo(() => problems.filter((problem) => problem.code !== 'NODE_INCOMPLETE'), [problems]);
  const incomplete = problems.filter((problem) => problem.code === 'NODE_INCOMPLETE' && problem.details?.field !== 'saveResponseTo.variable');
  return html`<div class="flow-node-form flow-automation-form bot-node-form" data-testid=${`bot-${node.type.toLowerCase().replace(/_/g, '-')}-form`}>
    <${AutomationActionFields} action=${action} objectMetadata=${object} context=${context} depth=${0}
      onChange=${(/** @type {Record<string, any>} */ next) => onReplaceConfig({ ...automationActionAsConfig(node.type, next), ...(isIntegration ? { saveResponseTo } : {}) })} />
    ${isIntegration
      ? html`<${BotSaveToField} value=${saveResponseTo} allowField=${false} variableError=${botFieldProblemText(problems, 'saveResponseTo.variable')}
          onChange=${(value) => onReplaceConfig({ ...node.config, saveResponseTo: value.variable ? { variable: value.variable } : null })} />
        <${FlowNotice}>${t('bots.forms.integration.notice')}<//>`
      : null}
    ${incomplete.length > 0 ? html`<p class="flow-field-error" role="alert">${t('bots.forms.integration.incomplete')}</p>` : null}
    ${otherProblems.map((problem) => html`<p key=${problem.code} class="flow-field-error" role="alert">${botProblemText(problem)}</p>`)}
  </div>`;
}
